import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { ApiResponse } from '../../@types/ApiResponse';
import type { Order } from '../../@types/Order';
import type { RequestStateQuery } from '../../@types/RequestStateQuery';
import { getJSON } from '../../fetch';
import useAuth from '../../hooks/useAuth';
import useBranch from '../branch/useBranch';

export const ONLINE_ORDERS_QUERY = '/api/orders';

export interface Params {
  limit: number;
  skip: number;
  orderStatus: string[];
}

interface Response {
  result: ApiResponse<Order[]>;
  params: URLSearchParams;
}

const Limit = 10;

const useOnlineOrders = (
  orderStatus: string[],
  limit = Limit,
): [Order[] | undefined, RequestStateQuery, (() => void) | undefined, boolean] => {
  const [branch] = useBranch();
  const { getToken } = useAuth();

  const { data, isLoading, isSuccess, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery<
      Response,
      unknown,
      InfiniteData<Response, unknown> | undefined,
      unknown[],
      string
    >({
      queryKey: [ONLINE_ORDERS_QUERY, limit, branch?.id, orderStatus.join(',')],
      queryFn: async ({ pageParam }) => {
        const urlSearchParams = new URLSearchParams();
        urlSearchParams.set('branches', branch?.id!);
        urlSearchParams.set('limit', limit.toString());
        urlSearchParams.set('skip', new URLSearchParams(pageParam || '').get('skip') || '0');
        orderStatus.forEach((status) => {
          urlSearchParams.append('statuses', status);
        });

        const query = urlSearchParams.toString();
        const result = await getJSON<ApiResponse<Order[]>>(`/api/orders?${query}`, getToken()!);

        return {
          result,
          params: urlSearchParams,
        };
      },
      enabled: !!branch?.id,
      getNextPageParam: (prev) => {
        if (prev.result.response.length < limit) return undefined;
        const nextParams = new URLSearchParams(prev.params);
        nextParams.set(
          'skip',
          (Number.parseInt(nextParams.get('skip') || '0', 10) + limit).toString(),
        );
        const r = prev.result.response.length ? nextParams.toString() : undefined;
        return r;
      },
      initialPageParam: '',
    });

  const concatData = useMemo(() => data?.pages.flatMap((x) => x.result.response), [data]);

  return [
    concatData,
    { isLoading, isSuccess, isError },
    hasNextPage ? fetchNextPage : undefined,
    isFetchingNextPage,
  ];
};

export default useOnlineOrders;
